import { InsuranceForTransfer } from "../../hooks/api/useGetCalculationById";
import {
  Box,
  Button,
  Flex,
  Loader,
  Paper,
  Skeleton,
  Text,
  Title,
  VisuallyHidden,
} from "@mantine/core";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import InsurancesListItem from "../insurancesListItem";
import ContactUsPaper from "../contactUsPaper";
import { useQueryClient } from "@tanstack/react-query";
import buttonClasses from "../../styles/buttons.module.css";

export function TransferStatus({
  advice = [],
  isSignInPreparation,
  isFetching,
  isLoading,
}: {
  advice: InsuranceForTransfer[];
  isSignInPreparation?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
}) {
  const queryClient = useQueryClient();
  const containsPhysicalSignature = advice?.find(
    (insurance: InsuranceForTransfer) => insurance.isDigitalTransfer === false,
  );
  const containsDigitalSignature = advice?.find(
    (insurance: InsuranceForTransfer) => insurance.isDigitalTransfer,
  );

  return (
    <Flex
      justify="center"
      align="center"
      mt={"2rem"}
      px={{ base: "xs", sm: 0 }}
    >
      <Flex direction="column" w="100%" maw="100%">
        <Title
          order={1}
          size="h2"
          mb="1rem"
          ff="var(--wallet-font-family-sambla-sans)"
        >
          Signera flyttdokument
        </Title>
        <Text>
          För att kunna påbörja flytten av alla dina pensionsförsäkringar så
          behöver du signera ett individuellt flyttdokument för varje
          försäkring. Försäkringsbolagen har olika krav på hur flyttdokumenten
          ska signeras. Du kan läsa mer nedanför.
        </Text>
        <Paper
          p={{ base: "md", sm: "lg" }}
          radius={"lg"}
          mb={"xl"}
          mt={"lg"}
          bg={"var(--wallet-color-masterbrand-accent-yellow)"}
        >
          {isSignInPreparation && (
            <Flex justify="center" align="center" role="status">
              <Loader
                size={"md"}
                m="lg"
                color="var(--wallet-color-neutrals-black)"
              />
              <Text m="lg">
                Vi förbereder dina signeringsdokument. Detta kan ta en minut.
              </Text>
            </Flex>
          )}
          {isLoading && (
            <>
              <Skeleton h="30px" my="md" />
              <Skeleton h="30px" my="md" />
              <Skeleton h="30px" my="md" />
              <Skeleton h="30px" my="md" />
            </>
          )}
          {!!containsPhysicalSignature && !isSignInPreparation && (
            <>
              <Title
                order={2}
                size="h4"
                mb={"md"}
                ff={"var(--wallet-font-family-sambla-sans)"}
              >
                Fysisk signering via post
              </Title>
              <Text mb="lg">
                En del försäkringsbolag erbjuder bara fysisk signering. Du
                kommer då att få dokument skickade hem till din brevlåda. Du
                signerar dokumenten och skickar sedan tillbaka med svarspost.
                Resten sköter vi.
              </Text>
            </>
          )}

          {!!containsDigitalSignature && !isSignInPreparation && (
            <>
              <Title
                order={2}
                size="h4"
                mb={"md"}
                ff={"var(--wallet-font-family-sambla-sans)"}
              >
                Digital signering med Byta Pension
              </Title>
              <Text mb="lg">
                En del försäkringsbolag erbjuder digital signering via Byta
                Pension. Du signerar enkelt med BankID. Vi har skickat ett mail
                till dig med en länk för signering, eller så använder du länken
                här nedanför.
              </Text>

              <Button
                fullWidth
                mb="xl"
                component="a"
                variant="default"
                className={buttonClasses.borderBottomOnly}
                href={`${import.meta.env.VITE_BYTAPENSION_URL}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Till digital signering
                <VisuallyHidden> (öppnas i ny flik)</VisuallyHidden>
                <ArrowTopRightOnSquareIcon
                  width="16px"
                  style={{ marginLeft: "10px" }}
                />
              </Button>
            </>
          )}

          {!isSignInPreparation && (
            <>
              <Title
                order={2}
                size="h4"
                mb={"md"}
                ff={"var(--wallet-font-family-sambla-sans)"}
              >
                Vad händer efter signering?
              </Title>
              <Text mb="lg">
                Så snart du har signerat dina ärenden digitalt och postalt så
                kommer vi att påbörja flytten av dina försäkringar till Idun
                Liv. Vi kommer att notifiera dig när flytten är klar och du
                kommer då att kunna se en sammanställning av din pension här i
                plattformen. Tveka inte på att höra av dig till oss om du har
                några funderingar kring signeringsprocessen.
              </Text>
            </>
          )}
        </Paper>
        <Paper p={{ base: "md", sm: "xl" }} radius={"lg"} mb={"xl"}>
          <Title
            ff={"var(--wallet-font-family-sambla-sans)"}
            order={3}
            mb={"md"}
          >
            Dessa försäkringarna flyttas till nya försäkringar hos Idun Liv
          </Title>

          {isFetching ? (
            <>
              <Skeleton h="35px" my="md" />
              <Skeleton h="35px" my="md" />
            </>
          ) : (
            <Box
              component="ul"
              role="list"
              style={{ listStyle: "none", margin: 0, padding: 0 }}
            >
              {advice.map((insurance: InsuranceForTransfer, index: number) => (
                <Box
                  component="li"
                  key={`${insurance.insuranceIdentifier}-${index}`}
                >
                  <InsurancesListItem
                    insurance={insurance}
                    isTransfer
                    isSignInPreparation={isSignInPreparation}
                  />
                </Box>
              ))}
            </Box>
          )}
          {!isSignInPreparation && (
            <Button
              variant="default"
              onClick={() =>
                queryClient.refetchQueries({
                  queryKey: ["get-transfer-policy"],
                })
              }
              loading={isFetching || isSignInPreparation}
              fullWidth
            >
              Uppdatera
            </Button>
          )}
        </Paper>
        <ContactUsPaper />
      </Flex>
    </Flex>
  );
}
